// DOM utilities - jQuery replacement
export const $ = (selector, ctx = document) => ctx.querySelector(selector);
export const $$ = (selector, ctx = document) => [...ctx.querySelectorAll(selector)];

export const on = (el, event, handler) => {
  el.addEventListener(event, handler);
  return () => el.removeEventListener(event, handler);
};

export const once = (el, event, handler) => {
  const onceHandler = e => {
    el.removeEventListener(event, onceHandler);
    handler(e);
  };
  el.addEventListener(event, onceHandler);
  return () => el.removeEventListener(event, onceHandler);
};

export const toggleClass = (el, className, force) => el.classList.toggle(className, force);
export const addClass = (el, className) => el.classList.add(className);
export const removeClass = (el, className) => el.classList.remove(className);
export const hasClass = (el, className) => el.classList.contains(className);

export const attr = (el, name, value) => {
  if (value === undefined) return el.getAttribute(name);
  el.setAttribute(name, value);
  return el;
};

export const removeAttr = (el, name) => {
  el.removeAttribute(name);
  return el;
};

export const html = (el, content) => {
  if (!el) return '';
  if (content === undefined) return el.innerHTML || '';
  el.innerHTML = content;
  return el;
};

export const text = (el, content) => {
  if (!el) return '';
  if (content === undefined) return el.textContent || '';
  el.textContent = content;
  return el;
};

export const css = (el, prop, value) => {
  if (!el?.style) return el;
  
  if (typeof prop === 'object') {
    Object.entries(prop).forEach(([k, v]) => el.style[k] = v);
    return el;
  }
  
  if (value === undefined) return getComputedStyle(el)[prop] || '';
  el.style[prop] = value;
  return el;
};

export const show = el => {
  el.style.display = '';
  return el;
};

export const hide = el => {
  el.style.display = 'none';
  return el;
};

/** Element is visible if not display:none and has non-zero layout size (works for fixed/sticky). */
export const isVisible = el => {
  if (!el) return false;
  const style = getComputedStyle(el);
  if (style.display === 'none') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

export const fadeIn = (el, duration = 300) => new Promise(resolve => {
  const style = getComputedStyle(el);
  if (style.display !== 'none' && parseFloat(style.opacity) >= 1) {
    resolve(el);
    return;
  }
  el.style.opacity = '0';
  el.style.display = '';
  
  let start = null;
  const animate = ts => {
    if (!start) start = ts;
    const progress = ts - start;
    el.style.opacity = Math.min(progress / duration, 1).toString();
    
    progress < duration ? requestAnimationFrame(animate) : resolve(el);
  };
  requestAnimationFrame(animate);
});

export const fadeOut = (el, duration = 300) => new Promise(resolve => {
  const initial = parseFloat(getComputedStyle(el).opacity);
  let start = null;
  
  const animate = ts => {
    if (!start) start = ts;
    const progress = ts - start;
    el.style.opacity = Math.max(initial - (progress / duration), 0).toString();
    
    if (progress < duration) {
      requestAnimationFrame(animate);
    } else {
      el.style.display = 'none';
      resolve(el);
    }
  };
  requestAnimationFrame(animate);
});

export const createElement = (tag, attrs = {}, content) => {
  const el = document.createElement(tag);
  
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === 'className') el.className = v;
    else if (k === 'textContent') el.textContent = v;
    else if (k === 'innerHTML') el.innerHTML = v;
    else if (k === 'style' && typeof v === 'object') {
      Object.entries(v).forEach(([sk, sv]) => { el.style[sk] = sv; });
    } else if (k.startsWith('data-')) el.setAttribute(k, v);
    else if (k.startsWith('on') && typeof v === 'function') {
      el.addEventListener(k.slice(2).toLowerCase(), v);
    } else el.setAttribute(k, v);
  });
  
  if (content !== undefined) {
    if (typeof content === 'string') el.innerHTML = content;
    else append(el, content);
  }
  
  return el;
};

/** Remove listener. Pass the same function reference that was passed to on() for proper teardown. */
export const off = (el, event, handler) => {
  if (handler) el.removeEventListener(event, handler);
};

export const append = (parent, ...children) => {
  if (!parent) return parent;
  for (const child of children) {
    if (Array.isArray(child)) {
      child.filter(Boolean).forEach(c => parent.appendChild(c));
    } else if (child) {
      parent.appendChild(child);
    }
  }
  return parent;
};

export const prepend = (parent, child) => {
  if (Array.isArray(child)) {
    child.reverse().forEach(c => parent.insertBefore(c, parent.firstChild));
  } else {
    parent.insertBefore(child, parent.firstChild);
  }
  return parent;
};

export const remove = el => el.parentNode?.removeChild(el);

export const closest = (el, selector) => el.closest(selector);

export const position = el => {
  const rect = el.getBoundingClientRect();
  return { top: rect.top + window.scrollY, left: rect.left + window.scrollX };
};

export const val = (el, value) => {
  if (value === undefined) return el.value;
  el.value = value;
  return el;
};

export const trigger = (el, eventName, detail = {}) => 
  el.dispatchEvent(new CustomEvent(eventName, { bubbles: true, cancelable: true, detail }));

export const ready = callback => 
  document.readyState !== 'loading' ? callback() : 
  document.addEventListener('DOMContentLoaded', callback);

export const delegate = (el, selector, event, handler) => {
  const delegated = e => {
    const target = e.target.closest(selector);
    if (target && el.contains(target)) handler.call(target, e);
  };
  el.addEventListener(event, delegated);
  return () => el.removeEventListener(event, delegated);
};

export const create = createElement;

/**
 * Observe a single element with IntersectionObserver.
 * @param {Element} element
 * @param {(entry: IntersectionObserverEntry) => void} callback
 * @param {{ once?: boolean, fallback?: (el: Element) => void, root?: Element|null, rootMargin?: string, threshold?: number|number[] }} [options]
 * @returns {{ disconnect: () => void }}
 */
export const observeWhenVisible = (element, callback, options = {}) => {
  const noop = { disconnect: () => {} };
  if (!element) return noop;

  const { once = false, fallback, ...ioOptions } = options;

  if (typeof IntersectionObserver === 'undefined') {
    if (typeof fallback === 'function') fallback(element);
    return noop;
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      callback(entry);
      if (once) observer.unobserve(entry.target);
    });
  }, ioOptions);

  observer.observe(element);
  return { disconnect: () => observer.disconnect() };
};

/**
 * Observe multiple elements with a single shared IntersectionObserver.
 * @param {Element[]} elements - Initial elements to observe (may be empty).
 * @param {(target: Element, entry: IntersectionObserverEntry) => void} callback
 * @param {{ once?: boolean, fallback?: (el: Element) => void, root?: Element|null, rootMargin?: string, threshold?: number|number[] }} [options]
 * @returns {{ disconnect: () => void, observe: (el: Element) => void, unobserve: (el: Element) => void }}
 */
export const observeManyWhenVisible = (elements, callback, options = {}) => {
  const { once = true, fallback, ...ioOptions } = options;
  const noop = { disconnect: () => {}, observe: () => {}, unobserve: () => {} };

  if (typeof IntersectionObserver === 'undefined') {
    const els = Array.isArray(elements) ? elements : [];
    if (typeof fallback === 'function') els.forEach(el => fallback(el));
    return {
      disconnect: () => {},
      observe: el => el && typeof fallback === 'function' && fallback(el),
      unobserve: () => {},
    };
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      callback(entry.target, entry);
      if (once) observer.unobserve(entry.target);
    });
  }, ioOptions);

  (Array.isArray(elements) ? elements : []).forEach(el => el && observer.observe(el));

  return {
    disconnect: () => observer.disconnect(),
    observe: el => el && observer.observe(el),
    unobserve: el => el && observer.unobserve(el),
  };
};

// Export all functions as a single object for convenience
export const dom = {
  $,
  $$,
  on,
  once,
  toggleClass,
  addClass,
  removeClass,
  hasClass,
  attr,
  removeAttr,
  html,
  text,
  css,
  show,
  hide,
  isVisible,
  fadeIn,
  fadeOut,
  createElement,
  append,
  prepend,
  remove,
  closest,
  position,
  val,
  trigger,
  ready,
  delegate,
  create,
  off,
  observeWhenVisible,
  observeManyWhenVisible,
};
